import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { BrandMark } from '@/components/atoms'
import { apiFetch, ApiClientError } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { InterviewPanel } from '@/features/plan/InterviewPanel'
import { PlanCards, type PlanCard } from '@/features/plan/PlanCards'
import { loadDraft, saveDraft, clearDraft } from '@/lib/draft-store'

/**
 * /plan?project=:id&phase=interview|cards
 *
 * 인터뷰 종료 시점에 cards 가 Firestore 에 PUT 으로 저장됨.
 * mount 또는 새로고침 시 GET `/api/plan/:id` 로 복구 시도 — 저장된 카드가 있으면 그대로 사용.
 */

interface PlanSearch {
  project?: string
  seed?: string
  phase?: 'interview' | 'cards'
}

export const Route = createFileRoute('/plan')({
  component: PlanPage,
  validateSearch: (search: Record<string, unknown>): PlanSearch => ({
    project: typeof search.project === 'string' ? search.project : undefined,
    seed: typeof search.seed === 'string' ? search.seed : undefined,
    phase:
      search.phase === 'interview' || search.phase === 'cards'
        ? (search.phase as 'interview' | 'cards')
        : 'interview',
  }),
})

interface ServerPlanDoc {
  cards?: PlanCard[]
  successCriteria?: string[]
}

function PlanPage() {
  const { project: projectId, seed: seedId, phase: phaseFromUrl } = useSearch({ from: '/plan' })
  const navigate = useNavigate()
  const [phase, setPhase] = useState<'interview' | 'cards'>(phaseFromUrl ?? 'interview')
  const [cards, setCards] = useState<PlanCard[]>([])
  const [successCriteria, setSuccessCriteria] = useState<string[]>([])
  const [hydrating, setHydrating] = useState(true)
  const idToken = useAuthStore((s) => s.idToken)
  // plan v1.3 G1 — seedId 만 있으면 draft 모드 (Firestore 아직 없음). projectId 우선.
  const draftMode = !projectId && !!seedId

  // 새로고침 또는 phase=cards 로 직접 진입 시 Firestore 에서 저장된 cards 복구
  // draft 모드면 localStorage 의 cards 만 사용 (Firestore 없음).
  useEffect(() => {
    if (draftMode) {
      // localStorage draft 의 cards 가 있으면 cards 단계로 진입
      const parsed = loadDraft()
      if (Array.isArray(parsed?.cards) && parsed.cards.length > 0) {
        setCards(parsed.cards)
        setSuccessCriteria(parsed.successCriteria ?? [])
        setPhase('cards')
      }
      setHydrating(false)
      return
    }
    if (!projectId || !idToken) {
      setHydrating(false)
      return
    }
    // 프로젝트 전환 시 이전 successCriteria 가 다음 작품 카드뷰에 새어 들어가지 않도록 초기화.
    setSuccessCriteria([])
    let cancelled = false
    ;(async () => {
      try {
        const doc = await apiFetch<ServerPlanDoc>(`/api/plan/${projectId}`)
        if (cancelled) return
        if (Array.isArray(doc.cards) && doc.cards.length > 0) {
          setCards(doc.cards)
          setSuccessCriteria(doc.successCriteria ?? [])
          // 저장된 cards 가 있으면 phase 강제로 'cards' 로
          if (phaseFromUrl !== 'cards') {
            setPhase('cards')
            void navigate({
              to: '/plan',
              search: { project: projectId, phase: 'cards' },
              replace: true,
            })
          }
        }
      } catch (err) {
        // 404 NO_PLAN_DOC 은 정상 (아직 저장 안 됨)
        if (!(err instanceof ApiClientError && err.status === 404)) {
          console.warn('plan.hydrate_failed', err)
        }
      } finally {
        if (!cancelled) setHydrating(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [draftMode, projectId, idToken, phaseFromUrl, navigate])

  if (!projectId && !seedId) {
    return (
      <div className="text-text-2 grid h-[calc(100vh-56px)] place-items-center">
        프로젝트가 선택되지 않았어요. 처음으로 돌아가서 다시 골라봐요.
      </div>
    )
  }

  if (hydrating) {
    return (
      <div className="text-text-3 grid h-[calc(100vh-56px)] place-items-center text-[13px]">
        계획을 불러오는 중이에요…
      </div>
    )
  }

  return (
    <div className="flex h-[calc(100vh-56px)] flex-col">
      <header className="border-border flex items-center gap-3 border-b bg-white px-5 py-3">
        <BrandMark size={26} />
        <span className="text-[15px] font-extrabold tracking-tight">계획 세우기</span>
        <span className="text-text-3 ml-auto text-[12px]">
          {phase === 'interview' ? '1단계 · 인터뷰' : '2단계 · 계획 확인'}
        </span>
      </header>
      <div className="min-h-0 flex-1">
        {phase === 'interview' ? (
          <InterviewPanel
            projectId={projectId ?? ''}
            seedId={seedId}
            draftMode={draftMode}
            onInterviewDone={async (collectedCards) => {
              let finalCards = collectedCards
              let finalSuccessCriteria: string[] = []
              if (draftMode && seedId) {
                try {
                  const cachedDoc = await apiFetch<{
                    cards: PlanCard[]
                    successCriteria: string[]
                  }>(`/api/plan/cached-cards-by-seed/${seedId}`)
                  if (Array.isArray(cachedDoc.cards) && cachedDoc.cards.length > 0) {
                    finalCards = cachedDoc.cards
                    finalSuccessCriteria = cachedDoc.successCriteria ?? []
                  }
                } catch (err) {
                  if (!(err instanceof ApiClientError && err.status === 404)) {
                    console.warn('plan.cached_cards_by_seed_fetch_failed', err)
                  }
                }
              } else if (projectId) {
                try {
                  const cachedDoc = await apiFetch<{
                    cards: PlanCard[]
                    successCriteria: string[]
                  }>(`/api/plan/cached-cards/${projectId}`)
                  if (Array.isArray(cachedDoc.cards) && cachedDoc.cards.length > 0) {
                    finalCards = cachedDoc.cards
                    finalSuccessCriteria = cachedDoc.successCriteria ?? []
                    void apiFetch(`/api/plan/cards/${projectId}`, {
                      method: 'PUT',
                      body: JSON.stringify({
                        cards: finalCards,
                        successCriteria: finalSuccessCriteria,
                      }),
                    }).catch(() => undefined)
                  }
                } catch (err) {
                  if (!(err instanceof ApiClientError && err.status === 404)) {
                    console.warn('plan.cached_cards_fetch_failed', err)
                  }
                }
              }
              setSuccessCriteria(finalSuccessCriteria)
              if (draftMode) {
                const draft = loadDraft()
                if (draft)
                  saveDraft({ ...draft, cards: finalCards, successCriteria: finalSuccessCriteria })
              }
              setCards(finalCards)
              setPhase('cards')
              void navigate({
                to: '/plan',
                search: projectId
                  ? { project: projectId, phase: 'cards' }
                  : { seed: seedId, phase: 'cards' },
                replace: true,
              })
            }}
          />
        ) : (
          <PlanCards
            projectId={projectId}
            seedId={seedId}
            cards={cards}
            successCriteria={successCriteria}
            onConfirm={() => {
              if (projectId) {
                void navigate({ to: '/ide', search: { project: projectId }, replace: true })
              }
            }}
            onCommitted={(newProjectId) => {
              // G1 draft commit 후 — Firestore doc + R2 + fileMeta 모두 생성됨. 현재 draft 정리.
              clearDraft()
              void navigate({ to: '/ide', search: { project: newProjectId }, replace: true })
            }}
          />
        )}
      </div>
    </div>
  )
}
